import React, { useCallback, useEffect, useState } from "react";
import {
  listPlatformUserInvitations,
  resendPlatformUserInvitation,
  revokePlatformUserInvitation,
  sendPlatformUserInvitation,
} from "../../api/api";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "../notifications_page/NotificationTabShared";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";

const fieldClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

const STATUS_LABELS = {
  pending: "Pending",
  claimed: "Joined",
  expired: "Expired",
  revoked: "Revoked",
};

/**
 * Platform outreach: invite one person to claim an individual account.
 * @param {{ adminId: number }} props
 * @returns {JSX.Element}
 */
function PlatformUserInvitationsTab({ adminId }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [preApproved, setPreApproved] = useState(true);
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState(0);
  const [revokingId, setRevokingId] = useState(0);

  const loadInvitations = useCallback(async () => {
    if (!adminId) return;
    setLoadingList(true);
    setError("");
    const result = await listPlatformUserInvitations(adminId);
    if (result.error) {
      setError(result.error);
      setItems([]);
    } else {
      setItems(Array.isArray(result.data) ? result.data : []);
    }
    setLoadingList(false);
  }, [adminId]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  /**
   * @returns {Promise<void>}
   */
  async function onSend() {
    setSending(true);
    setError("");
    const result = await sendPlatformUserInvitation(adminId, {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      pre_approved: preApproved,
    });
    setSending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setEmail("");
    setFirstName("");
    setLastName("");
    setPreApproved(true);
    await loadInvitations();
  }

  /**
   * @param {number} invitationId
   * @returns {Promise<void>}
   */
  async function onResend(invitationId) {
    setResendingId(invitationId);
    setError("");
    const result = await resendPlatformUserInvitation(adminId, invitationId);
    setResendingId(0);
    if (result.error) {
      setError(result.error);
      return;
    }
    await loadInvitations();
  }

  /**
   * @param {number} invitationId
   * @returns {Promise<void>}
   */
  async function onRevoke(invitationId) {
    setRevokingId(invitationId);
    setError("");
    const result = await revokePlatformUserInvitation(adminId, invitationId);
    setRevokingId(0);
    if (result.error) {
      setError(result.error);
      return;
    }
    await loadInvitations();
  }

  return (
    <div>
      <NotificationTabHeader
        title="Individual invitations"
        description="Invite a person to create their own account. The email links to a short join page. Pre-approved invites can use Assembly immediately."
      />

      {error ? <NotificationErrorPanel message={error} /> : null}

      <div className="mb-6 space-y-3 rounded-lg border border-ui-border bg-ui-card p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="individual-invitee-first-name" className="mb-1 block text-sm font-medium text-text-primary">
              First name
            </label>
            <input
              id="individual-invitee-first-name"
              type="text"
              autoComplete="given-name"
              className={fieldClass}
              placeholder="Jane"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="individual-invitee-last-name" className="mb-1 block text-sm font-medium text-text-primary">
              Last name
            </label>
            <input
              id="individual-invitee-last-name"
              type="text"
              autoComplete="family-name"
              className={fieldClass}
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="individual-invitee-email" className="mb-1 block text-sm font-medium text-text-primary">
              Email
            </label>
            <input
              id="individual-invitee-email"
              type="email"
              autoComplete="email"
              className={fieldClass}
              placeholder="jane@church.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Checkbox
            id="individual-pre-approve"
            checked={preApproved}
            onCheckedChange={setPreApproved}
            className="mt-0.5"
          />
          <label htmlFor="individual-pre-approve" className="text-sm text-text-secondary">
            Pre-approve this account so they can use Assembly as soon as they join.
          </label>
        </div>
        <Button
          type="button"
          onClick={() => onSend()}
          disabled={
            sending || !firstName.trim() || !lastName.trim() || !email.trim() || !adminId
          }
        >
          {sending ? "Sending…" : "Send invitation"}
        </Button>
      </div>

      {loadingList ? (
        <NotificationEmptyState title="Loading invitations…" className="border-solid bg-ui-card" />
      ) : null}

      {!loadingList && items.length === 0 ? (
        <NotificationEmptyState
          title="No invitations yet"
          subtitle="Sent invitations stay here so you can resend or revoke them."
          className="border-solid bg-ui-card"
        />
      ) : null}

      {!loadingList && items.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">Invitations</h3>
          {items.map((row) => {
            const displayName = `${row.first_name || ""} ${row.last_name || ""}`.trim();
            const statusLabel = STATUS_LABELS[row.status] || row.status;
            const canResend = row.status !== "claimed";
            const canRevoke = row.status === "pending" || row.status === "expired";
            return (
              <NotificationRequestCard key={row.id}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <h4 className="truncate text-lg font-semibold text-text-primary">{displayName}</h4>
                    <p className="truncate text-sm text-text-secondary">{row.email}</p>
                    <p className="text-xs text-text-muted">
                      {statusLabel}
                      {row.pre_approved ? " · Pre-approved" : " · Needs review"}
                      {" · Last sent "}
                      {row.last_sent_at
                        ? new Date(row.last_sent_at).toLocaleString(undefined, {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })
                        : "—"}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {canResend ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onResend(row.id)}
                        disabled={resendingId === row.id}
                      >
                        {resendingId === row.id ? "Sending…" : "Resend"}
                      </Button>
                    ) : null}
                    {canRevoke ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onRevoke(row.id)}
                        disabled={revokingId === row.id}
                      >
                        {revokingId === row.id ? "Revoking…" : "Revoke"}
                      </Button>
                    ) : null}
                  </div>
                </div>
              </NotificationRequestCard>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default PlatformUserInvitationsTab;
